<template>
  <div data-testid="page-meetings-requests">
    <MeetingsListLayout :loading="loading" :empty="appointments.length === 0" :groups="groupedAppointments">
      <template #toolbar>
        <h2 class="txt-headline-xl">{{ t('Meetings.requests.title') }}</h2>
        <SingleSelect v-model="selectedDay" :options="dayFilterOptions" :label="t('Meetings.myMeetings.filterByDay')" class="w-48" />
      </template>

      <template #empty>
        <img src="@/assets/images/meetings-list-placeholder.svg" alt="" class="mb-4 h-40" />
        <p class="text-body-m text-subtle">{{ t('Meetings.requests.noRequests') }}</p>
      </template>

      <template #card="{ item }">
        <MeetingCard
          :ref="(el) => setCardRef(item.id, el)"
          :appointment="item as AppointmentNode"
          mode="request"
          :action-loading="actionLoadingId === item.id"
          @accept="acceptRequest"
          @decline="handleDeclineClick"
          @save-notes="handleSaveNotes"
        />
      </template>
    </MeetingsListLayout>

    <!-- Decline Dialog -->
    <MeetingDeclineDialog :show="showDeclineDialog" @confirm="handleDeclineConfirm" @cancel="handleDeclineCancel" />
  </div>
</template>

<script setup lang="ts">
import { MeetingsListLayout } from '@ramp106/omrjs-events-ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import MeetingDeclineDialog from '@/components/Meetings/MeetingDeclineDialog.vue'
import { type AppointmentNode, useMeetingsRequests } from '@/composables/meetings/useMeetingsRequests'
import SingleSelect from '@/ui/SingleSelect.vue'

import MeetingCard from './components/MeetingCard.vue'

const { t } = useI18n()

const {
  selectedDay,
  loading,
  appointments,
  groupedAppointments,
  dayFilterOptions,
  actionLoadingId,
  acceptRequest,
  declineRequest,
  saveExhibitorNotes,
} = useMeetingsRequests()

// Card refs for resetting edit state
const cardRefs = ref<Record<string, InstanceType<typeof MeetingCard> | null>>({})

function setCardRef(id: string, el: unknown) {
  cardRefs.value[id] = el as InstanceType<typeof MeetingCard> | null
}

async function handleSaveNotes(appointmentId: string, notes: string) {
  const success = await saveExhibitorNotes(appointmentId, notes)
  cardRefs.value[appointmentId]?.resetEditingState(success)
}

const showDeclineDialog = ref(false)
const pendingDeclineId = ref<string | null>(null)

function handleDeclineClick(appointmentId: string) {
  pendingDeclineId.value = appointmentId
  showDeclineDialog.value = true
}

async function handleDeclineConfirm(reason: string) {
  if (pendingDeclineId.value) {
    await declineRequest(pendingDeclineId.value, reason || undefined)
  }
  showDeclineDialog.value = false
  pendingDeclineId.value = null
}

function handleDeclineCancel() {
  showDeclineDialog.value = false
  pendingDeclineId.value = null
}
</script>
